import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MailCheck, Send, AlertTriangle } from "lucide-react";

export default function EmailSettings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [senderValue, setSenderValue] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role !== "owner") return;
    api.getEmailSettings().then((d) => { setData(d); setSenderValue(d.sender); }).catch(() => {});
  }, [user]);

  if (user?.role !== "owner") return null;

  const save = async (withTest) => {
    setBusy(true);
    try {
      const r = await api.saveEmailSettings({ sender: senderValue.trim(), test_to: withTest ? testTo.trim() : "" });
      setData(r);
      if (r.test) {
        if (r.test.sent) toast.success(`Test email sent to ${testTo}`);
        else toast.error(r.test.reason || "Test email failed");
      } else {
        toast.success(`Sending as ${r.sender}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const onSharedSender = (data?.sender || "").endsWith("@resend.dev");

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-4" data-testid="email-settings-card">
      <div className="flex items-center gap-2">
        <MailCheck className="h-4 w-4 text-cyan-400" />
        <h3 className="font-display font-bold text-base">Outgoing Email</h3>
        <span className={`ml-auto font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
          data?.configured ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            : "border-rose-500/40 bg-rose-500/10 text-rose-300"}`}>
          {data?.configured ? "Resend connected" : "not configured"}
        </span>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Address used for invites, password resets and quota alerts. Verify your domain in Resend first, then set the
        sender to an address on that domain.
      </p>

      {onSharedSender && (
        <p className="flex items-start gap-2 text-[11px] font-mono text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
          Sending from the shared resend.dev address — Resend only delivers those to your own account email.
        </p>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] text-slate-400">Sender address</Label>
          <Input data-testid="email-sender-input" value={senderValue} onChange={(e) => setSenderValue(e.target.value)}
            placeholder={data?.default_sender || "noreply@yourdomain.com"}
            className="bg-[#0E1420] border-border font-mono text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] text-slate-400">Send a test to</Label>
          <Input data-testid="email-test-input" value={testTo} onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com" className="bg-[#0E1420] border-border font-mono text-sm" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="save-email-settings-button" onClick={() => save(false)} disabled={busy}
          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">Save sender</Button>
        <Button data-testid="send-test-email-button" variant="outline" onClick={() => save(true)}
          disabled={busy || !testTo.includes("@")}
          className="border-border bg-[#0E1420] hover:bg-slate-800 font-mono text-xs disabled:opacity-40">
          <Send className="h-3.5 w-3.5 mr-1.5" /> Save &amp; send test
        </Button>
      </div>
    </div>
  );
}
